import { Box, Divider, IconButton, Typography } from '@mui/material';
import CustomDrawer from 'components/shared/CustomDrawer/CustomDrawer';
import { LightTooltip } from 'components/shared/CustomTooltip/CustomTooltip';
import { ReactComponent as EditIcon } from 'assets/edit.svg';
import DeleteIcon from 'assets/delete-red.png';
import React, { useReducer } from 'react';
import zeplinColor from 'theme/Color';
import style from './ContactsView.module.css';
import ContactHeader from './ContactHeader/ContactHeader';
import ContactFields from './ContactFields/ContactFields';
import CustomNotes from 'components/shared/CustomNotes';
import { useContactNotes } from '../hooks/useContactNotes';
import { initialAlertState, useAlertStatus } from 'customHook/useAlertStatus';
import CustomAlert from 'components/shared/CustomAlert/CustomAlert';
import CustomDeleteModal from 'components/shared/CustomDeleteModal/CustomDeleteModal';
import { modalReducer } from 'reducers/modalReducer';
import { ContactService } from 'service/contact.service';

const ContactsView = ({
  setDrawerState,
  drawerState,
  selectedData,
  setAnchorEl,
  setAddContactOpen,
  notesData,
  onDeleteSuccess,
}: {
  setDrawerState: React.Dispatch<React.SetStateAction<boolean>>;
  drawerState: boolean;
  selectedData: any;
  setAnchorEl: React.Dispatch<React.SetStateAction<HTMLDivElement | null>>;
  setAddContactOpen: React.Dispatch<React.SetStateAction<boolean>>;
  notesData?: any;
  onDeleteSuccess: () => void;
}) => {
  const { alertState, startOperation, completeOperation, getAlertContent, setAlertState } =
    useAlertStatus();
  const [modalState, modalDispatch] = React.useReducer(modalReducer, { delete: false });
  const manager = useContactNotes(
    selectedData?.contactId || selectedData?.id,
    startOperation,
    completeOperation,
  );

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    modalDispatch({ type: 'OPEN', payload: 'delete' });
    setAnchorEl(null);
  };

  const handleConfirmDelete = async () => {
    try {
      await ContactService.deleteContact(selectedData?.contactId || selectedData?.id);

      modalDispatch({ type: 'CLOSE', payload: 'delete' });
      setDrawerState(false);
    } catch (error) {
      completeOperation('delete', 'Failed to delete contact', true);
      return;
    }
    onDeleteSuccess();
  };
  const onClose = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setDrawerState(open);
  };
  const handleEdit = () => {
    setAddContactOpen(true);
    setAnchorEl(null);
  };

  return (
    <CustomDrawer state={drawerState} onClose={onClose(false)}>
      <Box sx={{ width: { xs: '427px', xl: '527px' }, position: 'relative', overflowY: 'scroll' }}>
        <Box
          sx={{ position: 'sticky', top: 0, backgroundColor: zeplinColor.Neutral1, zIndex: '1' }}
        >
          <Box
            sx={{
              display: 'flex',

              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px',
            }}
          >
            <Typography variant='responsiveHeading2'>Contact Details</Typography>
            <Box
              sx={{
                display: 'flex',

                alignItems: 'center',
                gap: '12px',
              }}
            >
              <LightTooltip
                title={
                  <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                    Edit
                  </Typography>
                }
                placement='bottom'
              >
                <IconButton size='small' onClick={handleEdit}>
                  <EditIcon width={20} height={20} />
                </IconButton>
              </LightTooltip>
              <LightTooltip
                title={
                  <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                    Delete
                  </Typography>
                }
                placement='bottom'
              >
                <IconButton size='small' onClick={handleDelete}>
                  <img src={DeleteIcon} width={20} height={20} alt='delete' />
                </IconButton>
              </LightTooltip>
              <Divider
                orientation='vertical'
                flexItem
                sx={{
                  borderColor: zeplinColor.Neutral5,
                  height: '24px',
                  margin: '0 4px',
                }}
              />
              <img
                src={'/assets/close-icon.svg'}
                className={style['icon']}
                alt='close'
                style={{ cursor: 'pointer' }}
                onClick={onClose(false)}
              />
            </Box>
          </Box>
          <Divider
            component='div'
            sx={{ borderColor: zeplinColor.Neutral5, position: 'relative' }}
          />
        </Box>
        <Box sx={{ padding: { xs: '20px', xl: '24px' } }}>
          <Box sx={{ display: 'flex', gap: { xs: '16px', xl: '24px' }, flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', gap: { xs: '16px', xl: '32px' }, flexDirection: 'column' }}>
              <ContactHeader selectedData={selectedData} />

              <Divider
                component='div'
                sx={{ borderColor: zeplinColor.Neutral5, position: 'relative' }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
              <Typography variant='responsiveHeading3' sx={{ color: zeplinColor.BasicBlack }}>
                Personal Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}>
                <ContactFields
                  icon={'/assets/role.svg'}
                  fieldName='Client name'
                  fieldValue={selectedData?.name || 'N/A'}
                  isProfile
                  src={selectedData?.profileImageUrl || ''}
                />
                <ContactFields
                  icon={'/assets/company-icon.svg'}
                  fieldName='Company name'
                  fieldValue={selectedData?.company || 'N/A'}
                />
                <ContactFields
                  icon={'/assets/contact-card-icon.svg'}
                  fieldName='Role'
                  fieldValue={selectedData?.roles || 'N/A'}
                />
                <Box sx={{ display: 'flex', gap: '8px' }}>
                  <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <img
                      src={'/assets/call-icon.svg'}
                      alt='Contact'
                      width={'20px'}
                      height={'20px'}
                    />
                    <Typography
                      variant='responsiveBody3'
                      sx={{ color: zeplinColor.Neutral8, minWidth: '10rem' }}
                    >
                      Phone
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: '8px' }}>
                    <Typography variant='responsiveBody2'>
                      {selectedData?.phone || 'N/A'}
                    </Typography>
                    <img src='/assets/copy-view.png' alt='Contact' width={'18px'} height={'18px'} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: '8px' }}>
                  <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <img src={'/assets/email.svg'} alt='Contact' width={'20px'} height={'20px'} />
                    <Typography
                      variant='responsiveBody3'
                      sx={{ color: zeplinColor.Neutral8, minWidth: '10rem' }}
                    >
                      Email
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: '8px' }}>
                    <Typography variant='responsiveBody2' color={zeplinColor.Brand1}>
                      {selectedData?.email || 'N/A'}
                    </Typography>
                    <img src='/assets/copy-view.png' alt='Contact' width={'18px'} height={'18px'} />
                  </Box>
                </Box>
                <ContactFields
                  icon={'/assets/user-multiple.svg'}
                  fieldName='Manager'
                  fieldValue={
                    selectedData?.relatedTo?.account
                      ?.map((account: any) => account.Name)
                      .join(', ') || 'N/A'
                  }
                  isProfile
                  src={selectedData?.profileImageUrl}
                />
              </Box>
            </Box>
            <Divider
              component='div'
              sx={{ borderColor: zeplinColor.Neutral5, position: 'relative' }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
              <Typography variant='responsiveHeading3' sx={{ color: zeplinColor.BasicBlack }}>
                System Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}>
                <ContactFields
                  icon={'/assets/created-by-icon.svg'}
                  fieldName='Created by'
                  fieldValue={selectedData?.createdByName || 'N/A'}
                  isProfile
                  src={selectedData?.createdByProfileImageUrl}
                />
                <ContactFields
                  icon={'/assets/calendar-icon.svg'}
                  fieldName='Created date'
                  fieldValue={
                    selectedData?.createdAt
                      ? new Date(selectedData.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : 'N/A'
                  }
                />
                <ContactFields
                  icon={'/assets/clock-icon.svg'}
                  fieldName='Last contacted'
                  fieldValue='21 January 2026, 10:42 AM'
                />
              </Box>
            </Box>
            <Divider
              component='div'
              sx={{ borderColor: zeplinColor.Neutral5, position: 'relative' }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
              <CustomNotes manager={manager} />
            </Box>
          </Box>
        </Box>
      </Box>
      <CustomDeleteModal
        isModalOpen={modalState.delete}
        modalDispatch={modalDispatch}
        onDelete={handleConfirmDelete}
        title='Delete Contact'
        description='Are you sure you want to delete this contact? This action cannot be undone.'
      />
      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />
    </CustomDrawer>
  );
};

export default ContactsView;
